import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, cn } from '@saas-pages/shared/ui'
import { startToolOperation, trackToolRejection, type ToolAnalyticsContext, type ToolErrorCode } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT, type Translations } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  DPI_VALUES,
  MAX_FILE_MB,
  dpiLabel,
  imageFilename,
  packZip,
  renderPdfToImages,
  shouldSoftWarn,
  validateFileSize,
  zipFilename,
  type Dpi,
  type ImageOutputFormat,
  type RenderProgress,
} from '@/features/pdf-page-images/model/lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'
import { RevealOnMount } from '@/shared/ui/RevealOnMount'

type PdfImageFormCopy = Pick<
  Translations['vJpg'],
  | 'dpiLegend'
  | 'dpi72Hint'
  | 'dpi150Hint'
  | 'dpi300Hint'
  | 'openingPdf'
  | 'pageOf'
  | 'largeWarn'
  | 'submit'
  | 'loading'
  | 'again'
  | 'downloadZip'
  | 'previewAlt'
  | 'pagesLabel'
  | 'encrypted'
  | 'canvasUnavailable'
  | 'oom'
  | 'parseError'
> & {
  downloadImage: string
}

const FORMAT_CONFIG = {
  jpg: {
    utilityKey: 'vJpg',
    copyKey: 'vJpg',
  },
  png: {
    utilityKey: 'pdf-to-png',
    copyKey: 'pdfToPng',
  },
} as const satisfies Record<ImageOutputFormat, {
  utilityKey: 'vJpg' | 'pdf-to-png'
  copyKey: 'vJpg' | 'pdfToPng'
}>

type Phase =
  | { kind: 'idle' }
  | { kind: 'ready'; file: File }
  | { kind: 'loading'; file: File; progress: RenderProgress }
  | {
      kind: 'success'
      file: File
      blobs: Blob[]
      objectUrl: string
      previewDataUrl: string
      isZip: boolean
      totalBytes: number
    }
  | { kind: 'error'; file: File | null; message: string; errorCode: ToolErrorCode }

type DpiHintKey = 'dpi72Hint' | 'dpi150Hint' | 'dpi300Hint'

const DPI_HINT_KEY: Record<Dpi, DpiHintKey> = {
  72: 'dpi72Hint',
  150: 'dpi150Hint',
  300: 'dpi300Hint',
}

function getFormatCopy(t: Translations, format: ImageOutputFormat): PdfImageFormCopy {
  return t[FORMAT_CONFIG[format].copyKey]
}

// ─── Drop-zone ────────────────────────────────────────────────────────────────

interface DropZoneProps {
  onFile: (file: File) => void
  disabled: boolean
}

function DropZone({ onFile, disabled }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragging, setDragging] = useState(false)

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const file = files[0]
    if (!file) return
    if (!file.name.toLowerCase().endsWith('.pdf')) return
    onFile(file)
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={t.common.pickPdf}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => {
        if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          inputRef.current?.click()
        }
      }}
      onDragOver={(e) => {
        e.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(e.dataTransfer.files)
      }}
      className={cn(
        'flex flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 sm:p-10 text-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'cursor-pointer select-none transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="40"
        height="40"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-primary/60"
        aria-hidden="true"
      >
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>

      <div>
        <p className="text-sm font-medium text-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          {format(t.common.pdfLimit, { size: MAX_FILE_MB })}
        </p>
        <p className="mt-1 flex items-center justify-center gap-1 text-[11px] text-muted-foreground/80">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {t.common.localBadge}
        </p>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => handleFiles(e.target.files)}
        disabled={disabled}
      />
    </div>
  )
}

// ─── File badge ───────────────────────────────────────────────────────────────

interface FileBadgeProps {
  file: File
  onClear: () => void
  disabled: boolean
}

function FileBadge({ file, onClear, disabled }: FileBadgeProps) {
  const t = useT()
  const formatBytes = useFormatBytes()
  return (
    <div className="flex items-center gap-3 rounded-lg border bg-accent/30 px-4 py-3">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="shrink-0 text-primary"
        aria-hidden="true"
      >
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>

      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
        <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
      </div>

      <button
        type="button"
        onClick={onClear}
        disabled={disabled}
        aria-label={t.common.deleteFile}
        className={cn(
          'shrink-0 rounded p-1 text-muted-foreground transition-colors',
          'hover:bg-destructive/10 hover:text-destructive',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  )
}

// ─── DPI selector ─────────────────────────────────────────────────────────────

interface DpiSelectorProps {
  copy: PdfImageFormCopy
  name: string
  value: Dpi
  onChange: (v: Dpi) => void
  disabled: boolean
}

function DpiSelector({ copy, name, value, onChange, disabled }: DpiSelectorProps) {
  return (
    <fieldset disabled={disabled} className="space-y-2">
      <legend className="mb-2 text-sm font-medium text-foreground">{copy.dpiLegend}</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {DPI_VALUES.map((dpi) => {
          const selected = value === dpi
          return (
            <label
              key={dpi}
              className={cn(
                'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 transition-colors',
                selected
                  ? 'border-primary bg-primary/5 ring-1 ring-primary'
                  : 'border-border hover:border-primary/40 hover:bg-accent/20',
                disabled && 'pointer-events-none opacity-50',
              )}
            >
              <input
                type="radio"
                name={name}
                value={dpi}
                checked={selected}
                onChange={() => onChange(dpi)}
                className="sr-only"
              />
              <span className="text-sm font-medium text-foreground">{dpiLabel(dpi)}</span>
              <span className="text-xs text-muted-foreground">{copy[DPI_HINT_KEY[dpi]]}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

// ─── Progress ─────────────────────────────────────────────────────────────────

function ProgressPanel({ copy, progress }: { copy: PdfImageFormCopy; progress: RenderProgress }) {
  const ratio =
    progress.total === 0 ? 0 : Math.min(100, Math.round((progress.current / progress.total) * 100))
  return (
    <div className="rounded-lg border bg-accent/20 p-4">
      <div className="mb-2 flex items-center justify-between text-sm">
        <span className="font-medium text-foreground">
          {progress.total === 0
            ? copy.openingPdf
            : format(copy.pageOf, { current: progress.current, total: progress.total })}
        </span>
        <span className="text-muted-foreground">{ratio}%</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-border">
        <div
          className="h-full bg-primary transition-[width] duration-150 ease-out"
          style={{ width: `${ratio}%` }}
        />
      </div>
    </div>
  )
}

// ─── Result ───────────────────────────────────────────────────────────────────

interface ResultPanelProps {
  format: ImageOutputFormat
  copy: PdfImageFormCopy
  file: File
  blobs: Blob[]
  objectUrl: string
  previewDataUrl: string
  isZip: boolean
  totalBytes: number
  onReset: () => void
}

function ResultPanel({
  format: outputFormat,
  copy,
  file,
  blobs,
  objectUrl,
  previewDataUrl,
  isZip,
  totalBytes,
  onReset,
}: ResultPanelProps) {
  const t = useT()
  const formatBytes = useFormatBytes()

  function handleDownload() {
    const a = document.createElement('a')
    a.href = objectUrl
    a.download = isZip ? zipFilename(file.name) : imageFilename(file.name, 1, 1, outputFormat)
    a.click()
  }

  return (
    <Card className="border-green-200 bg-green-50/50 dark:border-green-500/30 dark:bg-green-500/10">
      <CardContent className="p-5">
        <RevealOnMount />
        <div className="mb-4 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-green-600 dark:text-green-300"
            aria-hidden="true"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <span className="text-base font-semibold text-green-700 dark:text-green-200">{t.common.done}</span>
        </div>

        {previewDataUrl && (
          <div className="mb-4 overflow-hidden rounded-md border bg-background">
            <img
              src={previewDataUrl}
              alt={copy.previewAlt}
              className="mx-auto block max-h-64 w-auto object-contain"
            />
          </div>
        )}

        <div className="mb-4 space-y-1.5 text-sm">
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.file}</span>
            <span className="truncate text-right font-medium text-foreground">{file.name}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{copy.pagesLabel}</span>
            <span className="font-medium text-foreground">{blobs.length}</span>
          </div>
          <div className="flex justify-between gap-2">
            <span className="text-muted-foreground">{t.common.size}</span>
            <span className="font-medium text-foreground">{formatBytes(totalBytes)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Button onClick={handleDownload} size="lg" className="flex-1">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            {isZip ? copy.downloadZip : copy.downloadImage}
          </Button>
          <Button variant="outline" onClick={onReset} className="flex-1 sm:flex-none">
            {copy.again}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Error ────────────────────────────────────────────────────────────────────

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT()
  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="shrink-0 text-destructive"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span className="text-sm font-semibold text-destructive">{t.common.errorTitle}</span>
        </div>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

function classifyError(
  err: unknown,
  copy: PdfImageFormCopy,
): { code: ToolErrorCode; message: string } {
  const text = err instanceof Error ? err.message : String(err)
  if (/password|encrypted/i.test(text)) {
    return { code: 'unsupported_input', message: copy.encrypted }
  }
  if (/canvas-unavailable/.test(text)) {
    return { code: 'processing_failed', message: copy.canvasUnavailable }
  }
  if (/encode-failed|out of memory/i.test(text)) {
    return { code: 'processing_failed', message: copy.oom }
  }
  return { code: 'invalid_input', message: copy.parseError }
}

// ─── Main form ────────────────────────────────────────────────────────────────

interface PdfPageImagesFormProps {
  outputFormat?: ImageOutputFormat
}

export function PdfPageImagesForm({ outputFormat = 'jpg' }: PdfPageImagesFormProps) {
  const t = useT()
  const copy = getFormatCopy(t, outputFormat)
  const definition = getUtilityDefinition(FORMAT_CONFIG[outputFormat].utilityKey)
  const analytics = {
    app: 'pdfy',
    domain: 'pdfy.ru',
    route: definition.path,
    utility: definition.analyticsKey,
    processingMode: 'local',
  } satisfies ToolAnalyticsContext
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' })
  const [dpi, setDpi] = useState<Dpi>(150)
  const abortRef = useRef<AbortController | null>(null)
  const objectUrlRef = useRef<string | null>(null)

  useEffect(() => () => {
    abortRef.current?.abort()
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current)
  }, [])

  const isLoading = phase.kind === 'loading'
  const hasFile = phase.kind === 'ready' || phase.kind === 'loading' || phase.kind === 'error'
  const currentFile = hasFile ? phase.file : null

  function handleFile(file: File) {
    abortRef.current?.abort()
    abortRef.current = null
    if (!validateFileSize(file)) {
      trackToolRejection(analytics, 'size_limit')
      setPhase({
        kind: 'error',
        file,
        errorCode: 'size_limit',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      })
      return
    }
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
      objectUrlRef.current = null
    }
    setPhase({ kind: 'ready', file })
  }

  function handleClear() {
    if (phase.kind === 'success') {
      URL.revokeObjectURL(phase.objectUrl)
      objectUrlRef.current = null
    }
    abortRef.current?.abort()
    setPhase({ kind: 'idle' })
  }

  function handleRetry() {
    if (phase.kind === 'error' && phase.file) {
      setPhase({ kind: 'ready', file: phase.file })
    } else {
      setPhase({ kind: 'idle' })
    }
  }

  async function handleSubmit() {
    if (phase.kind !== 'ready') return
    const file = phase.file

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setPhase({ kind: 'loading', file, progress: { current: 0, total: 0 } })
    const operation = startToolOperation(analytics)
    const startMs = Date.now()

    try {
      const { blobs, previewDataUrl, totalBytes } = await renderPdfToImages({
        file,
        dpi,
        format: outputFormat,
        signal: controller.signal,
        onProgress: (progress) => {
          if (controller.signal.aborted) return
          setPhase((prev) => (prev.kind === 'loading' ? { ...prev, progress } : prev))
        },
      })

      const firstBlob = blobs[0]
      if (!firstBlob) throw new Error('parse_error')
      if (controller.signal.aborted) return

      const isZip = blobs.length > 1
      const downloadBlob = isZip ? await packZip(file.name, blobs, outputFormat) : firstBlob
      if (controller.signal.aborted) return
      const objectUrl = URL.createObjectURL(downloadBlob)
      objectUrlRef.current = objectUrl
      const durationMs = Date.now() - startMs

      if (controller.signal.aborted) {
        URL.revokeObjectURL(objectUrl)
        objectUrlRef.current = null
        return
      }
      operation.success(durationMs)

      setPhase({
        kind: 'success',
        file,
        blobs,
        objectUrl,
        previewDataUrl,
        isZip,
        totalBytes,
      })
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') return
      if (controller.signal.aborted) return
      const { code, message } = classifyError(err, copy)
      if (controller.signal.aborted) return
      operation.error(code)
      setPhase({ kind: 'error', file, errorCode: code, message })
    }
  }

  if (phase.kind === 'success') {
    return (
      <div className="space-y-4">
        <ResultPanel
          format={outputFormat}
          copy={copy}
          file={phase.file}
          blobs={phase.blobs}
          objectUrl={phase.objectUrl}
          previewDataUrl={phase.previewDataUrl}
          isZip={phase.isZip}
          totalBytes={phase.totalBytes}
          onReset={handleClear}
        />
      </div>
    )
  }

  const showSoftWarn =
    phase.kind === 'loading' && phase.progress.total > 0 && shouldSoftWarn(phase.progress.total)

  return (
    <div className="space-y-5">
      {currentFile ? (
        <FileBadge file={currentFile} onClear={handleClear} disabled={isLoading} />
      ) : (
        <DropZone onFile={handleFile} disabled={isLoading} />
      )}

      <DpiSelector
        copy={copy}
        name={`pdf-image-${outputFormat}-dpi`}
        value={dpi}
        onChange={setDpi}
        disabled={isLoading}
      />

      {phase.kind === 'loading' && <ProgressPanel copy={copy} progress={phase.progress} />}
      {showSoftWarn && (
        <p className="text-xs text-muted-foreground">{copy.largeWarn}</p>
      )}

      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={handleRetry} />}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'ready' || isLoading}
        onClick={handleSubmit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {copy.loading}
          </>
        ) : (
          copy.submit
        )}
      </Button>
    </div>
  )
}
